import {ethers, JsonRpcProvider} from 'ethers';
import {CdkFacet} from './contract/facet/cdk.facet';
import {RoundFactoryFacet} from './contract/facet/round-factory.facet';
import {RoundFacet} from './contract/facet/round.facet';
import {StakingFacet} from './contract/facet/staking-facet';
import {CdkDiamonds} from './contract/cdk-diamonds';
import {DiamondInit} from './contract/diamond-init';
import {USDC} from './contract/usdc';
import {StakingFactoryFacet} from './contract/facet/staking-factory.facet';
import {isRoundStatus, isRoundType, isStakingType, RoundStakingStatus, RoundType, Stake, StakingData, StakingType} from './model/staking.model';
import {RoundInfo} from './model/round.model';
import {SaleRound, StakingInfo} from '../model/round.model';
import {delay, runWithDelay} from '../utils/async-utils';
import {MetaMaskSDK, SDKProvider} from "@metamask/sdk";
import Decimal from 'decimal.js';
import { mapStakingData } from './mappers/staking.mapper';


const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS!;
    const providerUrl = process.env.REACT_APP_PROVIDER_URL!;
const faucetAddress = process.env.REACT_APP_FAUCET_ADDRESS!;
const privateKey = process.env.REACT_APP_PRIVATE_KEY;
const grantSuperAdminAddress = process.env.REACT_APP_SUPER_ADMIN_ADDRESS;
const provider = new JsonRpcProvider(providerUrl);
const jobUpdaterPrivateKey = process.env.REACT_APP_JOB_UPDATER_PRIVATE_KEY;
export const buildCdkContract = (): CDKContract => {
    return new CDKContract(contractAddress, faucetAddress, provider);
}

export const NO_SALE_ROUND = 'no-round';

export type UserStakeAndRound = Stake & { saleRoundId: SaleRound['roundId'], stakingRoundId: StakingInfo['stakingId'] };
export type StakingInfoAndUserStake = {
    stakingInfo: StakingInfo;
    countCDKStaked: number;
    rewards: number;
}

export class EthereumManager {
    private static instance: EthereumManager;
    private readonly provider?: SDKProvider;
    private readonly errorMessage: string = '';

    private constructor() {
        const provider = window.ethereum; // desktop
        if (provider !== undefined) {
            this.provider = provider as SDKProvider;
            return;
        }


        const MMSDK = new MetaMaskSDK({
            dappMetadata: {
                name: 'CDK User Dapp',
                url: window.location.href,
            },
            injectProvider: true
        })

        const mobileProvider = MMSDK.getProvider();
        if (mobileProvider === undefined) {
            this.errorMessage = 'MetaMask SDK provider is undefined. Please install the MetaMask SDK.';
        } else {
            this.provider = mobileProvider;
        }
    }

    public static getInstance(): EthereumManager {
        if (!EthereumManager.instance) {
            EthereumManager.instance = new EthereumManager();
        }
        return EthereumManager.instance;
    }

    public getProvider(): SDKProvider {
        if (!this.provider) {
            throw new Error(this.errorMessage);
        }
        return this.provider;
    }

    public hasProvider(): boolean {
        return this.provider !== undefined;
    }
}

export const hasEthereum = () => {
    return EthereumManager.getInstance().hasProvider();
}
export const getEthereum = () => {
    return EthereumManager.getInstance().getProvider();
}


export class CDKContract {
    readonly provider: JsonRpcProvider;

    // Facets
    readonly cdkFacet: CdkFacet;
    readonly roundFactoryFacet: RoundFactoryFacet;
    readonly roundFacet: RoundFacet;
    readonly stakingFacet: StakingFacet;
    readonly stakingFactoryFacet: StakingFactoryFacet

    // Autres contrats
    readonly diamonds: CdkDiamonds;
    readonly diamondInit: DiamondInit;
    readonly usdc: USDC;
    readonly diamondAddress: string;

    readonly saleRoundHelper: SaleRoundHelper

    constructor(
        diamondAddress: string, 
        usdcFaucetAddress: string,
        provider: JsonRpcProvider
    ) {
        // Initialisation des facets
        this.cdkFacet = new CdkFacet(diamondAddress, provider);
        this.roundFactoryFacet = new RoundFactoryFacet(diamondAddress, provider);
        this.roundFacet = new RoundFacet(diamondAddress, provider);
        this.stakingFacet = new StakingFacet(diamondAddress, provider);
        this.stakingFactoryFacet = new StakingFactoryFacet(diamondAddress, provider);

        // Initialisation des autres contrats
        this.diamonds = new CdkDiamonds(diamondAddress, provider);
        this.diamondInit = new DiamondInit(diamondAddress, provider);
        this.usdc = new USDC(usdcFaucetAddress, provider);

        this.diamondAddress = diamondAddress;
        this.provider = provider;

        this.saleRoundHelper = new SaleRoundHelper(this.roundFactoryFacet, this.roundFacet, this.stakingFacet);
    }

    // Méthode d'achat de tokens CDK
    buyCDKTokens = async (roundId: string, cdkAmount: number, tokenPrice: number) => {
        const usdcAmountToSpend = Number(new Decimal(tokenPrice)
                                                .times(cdkAmount)
                                                .toFixed(5));
        // await this.usdc.approve('0xa8F3Ba52852c4647412c73E3A5268a64c7D11E28', usdcAmountToSpend);
        await this.usdc.approve(this.diamonds.address, usdcAmountToSpend);
        await this.roundFacet.doBuyTokens(roundId, cdkAmount);
    }

    getLastPublicActiveSaleRound = async (): Promise<RoundInfo | null> => {
        try {
            // Call the same method that handles signing
            const saleRounds = await this.roundFactoryFacet.getAllSaleRoundDetails()
            if (saleRounds.length === 0) {
                return null;
            }
            return saleRounds[saleRounds.length - 1];
        } catch (error) {
            console.error('Error getting last public active sale round:', error);
            return null;
        }
    }

    retrieveAllPublicActiveSaleRounds = async (): Promise<RoundInfo[]> => {
        const saleRounds = await this.roundFactoryFacet.getAllSaleRoundDetails();
        const matchingRounds = saleRounds.filter(round =>
            isRoundStatus(round.roundDetails.roundStatus, RoundStakingStatus.ON)
             && isRoundType(round.roundDetails.roundType, RoundType.PUBLIC));
        return matchingRounds;
    }

    retrieveUserStakes = async (walletAddress: string, stakingRounds: StakingInfo[]): Promise<StakingInfoAndUserStake[]> => {
        return await Promise.all(stakingRounds.map(async stakingInfo => {
            // Get user stakes and log the result
            const userStakes = await this.stakingFacet.getUserStakes(stakingInfo.stakingId, walletAddress);
            const countCDKStaked = userStakes.reduce(
                (acc, currentValue) => acc + Number(currentValue.amount), 0
            );
            
            // Calculate rewards for this staking round
            const rewards = await this.stakingFacet.doCalculateRewards(stakingInfo.stakingId, walletAddress);
            
            delay(100);
            return {stakingInfo, countCDKStaked, rewards};
        }));
    }
            

    retrieveAllPublicActiveStakingRounds = async (): Promise<StakingInfo[]> => {
        const allActiveStaking = await this.getActiveStakingRounds();
        const matchingStakings = allActiveStaking.filter(round =>
            isRoundStatus(round.stakingDetails.stakingStatus, RoundStakingStatus.ON)
             && isStakingType(round.stakingDetails.stakingType, StakingType.MANUAL));
        // console.log("allActiveStaking", allActiveStaking.map(Stacking => mapStakingData(Stacking)));
        // const publicSaleRoundIds = (await this.retrieveAllPublicActiveSaleRounds()).map(saleRound => saleRound.roundId);
        // const getStakingRoundIds$ = publicSaleRoundIds.map(saleRoundId => () => this.roundFacet.getStakingId(saleRoundId));
        // const allStakingRoundIds = await runWithDelay(getStakingRoundIds$, 100);
        
        // const getStakingRoundDetails$ = allStakingRoundIds.map(stakingRoundId => () => {
        //     return new Promise<StakingInfo>((resolve, reject) => {
        //         this.stakingFacet.getStakingRoundDetails(stakingRoundId).then((roundDetails: StakingData) => {
        //                 const statingInfo: StakingInfo = {
        //                     stakingId: stakingRoundId,
        //                     stakingDetails: roundDetails
        //                 }
        //                 resolve(statingInfo);
        //             })
        //             .catch((error) => {
        //                 console.error(`Error fetching staking round details for ID ${stakingRoundId}:`, error);
        //                 reject(error);
        //             });
        //     })
        // });
        return matchingStakings;
    }

    retrieveAllPublicActiveUnStakingRounds = async (): Promise<StakingInfo[]> => {
        const allActiveUnStaking = await this.getActiveStakingRounds();
        return allActiveUnStaking;
    }

    getActiveStakingRounds = async () => {
        return await this.stakingFactoryFacet.getAllStakingDetails();
    }

    getLastActiveStakingRound = async () => {
        const stakingRounds = await this.getActiveStakingRounds();
        return stakingRounds[stakingRounds.length - 1];
    }
    
    
    getAllUserStakes = async (walletAddress: string): Promise<UserStakeAndRound[]> => {
        const userRounds = await this.stakingFacet.getUserRounds(walletAddress);
        const userStakes$ = userRounds.map(userRound => this.#getUserStakesForStakingRound(userRound.saleRoundId, userRound.stakingRoundId, walletAddress));
        return (await Promise.all(userStakes$)).flatMap((x: UserStakeAndRound[]) => x);
    }

    #getUserStakesForStakingRound = async (saleRoundId: string, stakingRoundId: string, walletAddress: string): Promise<UserStakeAndRound[]> => {
        const userStakesForStakingRound = await this.stakingFacet.getUserStakes(stakingRoundId, walletAddress);
        return userStakesForStakingRound.map(userStake => ({
            ...userStake,
            saleRoundId,
            stakingRoundId
        }));
    }

    loadAllSaleRounds = async (status?: RoundStakingStatus, roundType?: RoundType): Promise<RoundInfo[]>  => {
        return this.saleRoundHelper.loadAllSaleRounds(status, roundType)
    }

    loadAllSaleRoundsWithStakingDetails = async (status?: RoundStakingStatus, roundType?: RoundType): Promise<SaleRoundWithStaking[]>  => {
        return this.saleRoundHelper.loadAllSaleRoundsWithStaking(status, roundType);
    }

    // careful, this should return all past and current stakes for the user
    retrieveAllCDKStakedForUser = async(userWalletAddress: string): Promise<number> => {
        const allUserStakes = await this.getAllUserStakes(userWalletAddress);
        return allUserStakes.map(userStake => userStake.amount).reduce((acc, currentValue) => acc + Number(currentValue), 0);
    }

    retrieveAllWithdrawableAmount = async (userWalletAddress: string): Promise<number> => {
        const allUserRounds = await this.stakingFacet.getUserRounds(userWalletAddress);
        const allStakingRoundIds = new Set(allUserRounds.map(userRound => userRound.stakingRoundId));
        const allWithdrawableAmounts = Array.from(allStakingRoundIds).map(stakingRoundId => () => this.stakingFacet.getWithdrawableAmount(stakingRoundId, userWalletAddress));
        const allWithdrawableAmountsResults = await runWithDelay(allWithdrawableAmounts, 100);
        return allWithdrawableAmountsResults.reduce((acc, stake) => acc + Number(stake), 0);
    }

    doStakeTokens = async (stakingId: string, amount: number) => {
        await this.cdkFacet.approve(this.diamondAddress, amount);
        await this.stakingFacet.doStakeTokens(stakingId, amount);
    }
}

export type SaleRoundWithStaking = {
    saleRound: RoundInfo;
    stakingRound: StakingData;
}
class SaleRoundHelper {

    #roundFactoryFacet: RoundFactoryFacet;
    #roundFacet: RoundFacet;
    #stakingFacet: StakingFacet

    constructor(roundFactoryFacet: RoundFactoryFacet,
            roundFacet: RoundFacet,
            stakingFacet: StakingFacet
    ) {
        this.#roundFactoryFacet = roundFactoryFacet;
        this.#roundFacet = roundFacet;
        this.#stakingFacet = stakingFacet;
    }

    filterRounds = (salesRounds: RoundInfo[], status?: RoundStakingStatus, roundType?: RoundType): RoundInfo[] => {
        const statusFilter = status ? (round: RoundInfo) => round.roundDetails.roundStatus === status : (_: RoundInfo) => true;
        const roundTypeFilter = roundType ? (round: RoundInfo) => isRoundType(round.roundDetails.roundType, roundType) : (_: RoundInfo) => true;
        return salesRounds
            .filter(statusFilter)
            .filter(roundTypeFilter);
    }
    
    loadAllSaleRounds = async (status?: RoundStakingStatus, roundType?: RoundType): Promise<RoundInfo[]>  => {
        const rounds = await this.#roundFactoryFacet.getAllSaleRoundDetails()
        const filteredRounds = this.filterRounds(rounds, status, roundType);
        return filteredRounds;
    }

    loadAllSaleRoundsWithStaking = async (status?: RoundStakingStatus, roundType?: RoundType): Promise<SaleRoundWithStaking[]> => {
        const rounds = await this.#roundFactoryFacet.getAllSaleRoundDetails()
        const filteredRounds = this.filterRounds(rounds, status, roundType);
        const data = await Promise.all(filteredRounds.map(async saleRound => {
            const stakingRoundId = await this.#roundFacet.getStakingId(saleRound.roundId);
            const stakingRound = await this.#stakingFacet.getStakingRoundDetails(stakingRoundId);
            delay(100);
            return {saleRound, stakingRound}
        }));
        return data;
    };

}